import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiDeleteLabel, apiListEvents, type EventStatusFilter } from '../api';
import type { LabelEvent } from '../types';
import LabelHistoryModal from '../components/LabelHistoryModal';
import TaskManager from '../components/TaskManager';
import { fmtTime } from '../lib/format';

type Dir = 'asc' | 'desc';
type ServerSort =
  | 'id'
  | 'event_id'
  | 'task_id'
  | 'created_at'
  | 'labeled_at'
  | 'labeler';

const SERVER_COLUMNS: { key: ServerSort; label: string }[] = [
  { key: 'event_id', label: '事件 ID' },
  { key: 'task_id', label: '任務' },
  { key: 'labeled_at', label: '標註時間' },
  { key: 'labeler', label: '標註者' },
  { key: 'created_at', label: '建立時間' },
];

export default function Review() {
  // 後端查詢狀態
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [serverQ, setServerQ] = useState('');
  const [serverSort, setServerSort] = useState<ServerSort>('id');
  const [serverDir, setServerDir] = useState<Dir>('desc');
  // 由上方 TaskManager 的「選取」按鈕設定;null 代表全事件
  const [taskFilter, setTaskFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | EventStatusFilter>('');

  // 前端對本頁的搜尋/排序
  const [clientQ, setClientQ] = useState('');
  const [clientSort, setClientSort] = useState<string | null>(null);
  const [clientDir, setClientDir] = useState<Dir>('asc');

  const [items, setItems] = useState<LabelEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<{ eventPk: number; eventId: number } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiListEvents({
        page,
        page_size: pageSize,
        q: serverQ || undefined,
        sort: serverSort,
        dir: serverDir,
        task_id: taskFilter ?? undefined,
        status: statusFilter || undefined,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, serverSort, serverDir, taskFilter, statusFilter]);

  function onServerSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    load();
  }

  const filteredItems = useMemo(() => {
    const q = clientQ.trim().toLowerCase();
    let out = items;
    if (q) {
      out = out.filter(it => {
        const fields: string[] = [
          String(it.event_id),
          String(it.task_id),
          it.video_url,
          it.label?.labeler_email ?? '',
          it.label ? String(it.label.data.time_seconds) : '',
        ];
        return fields.some(f => f.toLowerCase().includes(q));
      });
    }
    if (clientSort) {
      out = [...out].sort((a, b) => {
        const va = getField(a, clientSort);
        const vb = getField(b, clientSort);
        if (va === vb) return 0;
        const cmp = va < vb ? -1 : 1;
        return clientDir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [items, clientQ, clientSort, clientDir]);

  function toggleClientSort(col: string) {
    if (clientSort === col) setClientDir(clientDir === 'asc' ? 'desc' : 'asc');
    else { setClientSort(col); setClientDir('asc'); }
  }
  function toggleServerSort(col: ServerSort) {
    if (serverSort === col) setServerDir(serverDir === 'asc' ? 'desc' : 'asc');
    else { setServerSort(col); setServerDir('asc'); }
    setPage(1);
  }

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="card">
      <h2>檢視</h2>

      <TaskManager
        selectedTaskId={taskFilter}
        onSelectTask={id => { setTaskFilter(id); setPage(1); }}
      />

      <hr className="section-divider" />

      <h3>
        事件
        {taskFilter !== null && (
          <span className="muted small"> · 已限縮在任務 #{taskFilter}</span>
        )}
      </h3>

      <div className="filters">
        <form onSubmit={onServerSearch} className="row">
          <label>
            資料庫搜尋
            <input
              type="text"
              placeholder="事件 ID 或標註者電子郵件"
              value={serverQ}
              onChange={e => setServerQ(e.target.value)}
            />
          </label>
          <button type="submit">套用</button>
        </form>

        <label>
          標註狀態
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as '' | EventStatusFilter); setPage(1); }}
          >
            <option value="">(全部)</option>
            <option value="labeled">已標註</option>
            <option value="unlabelable">無法標記</option>
            <option value="unlabeled">未標註</option>
          </select>
        </label>

        <label>
          每頁筆數
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      <div className="filters">
        <label>
          本頁搜尋
          <input
            type="text"
            placeholder="本頁任意欄位"
            value={clientQ}
            onChange={e => setClientQ(e.target.value)}
          />
        </label>
        <span className="muted">
          本頁顯示 {filteredItems.length} / {items.length} · 共 {total} 筆
        </span>
      </div>

      {error && <div className="error">{error}</div>}

      <table className="grid">
        <thead>
          <tr>
            {SERVER_COLUMNS.map(c => (
              <th key={c.key} onClick={() => toggleServerSort(c.key)} className="sortable">
                {c.label}
                {serverSort === c.key && (serverDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
            <th onClick={() => toggleClientSort('time_seconds')} className="sortable">
              時間 (秒)
              {clientSort === 'time_seconds' && (clientDir === 'asc' ? ' ▲' : ' ▼')}
            </th>
            <th>影片</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={8}>載入中…</td></tr>
          ) : filteredItems.length === 0 ? (
            <tr><td colSpan={8}>無資料</td></tr>
          ) : (
            filteredItems.map(it => (
              <tr key={it.id}>
                <td>{it.event_id}</td>
                <td>{it.task_id}</td>
                <td>{fmtTime(it.label?.labeled_at)}</td>
                <td>{it.label?.labeler_email ?? '—'}</td>
                <td>{fmtTime(it.created_at)}</td>
                <td>
                  {it.label
                    ? it.label.data.unlabelable
                      ? (
                          <span
                            className="badge badge-deleted"
                            title={it.label.data.reason || undefined}
                          >
                            無法標記
                          </span>
                        )
                      : typeof it.label.data.time_seconds === 'number'
                        ? it.label.data.time_seconds.toFixed(3)
                        : '—'
                    : '—'}
                </td>
                <td><a href={it.video_url} target="_blank" rel="noreferrer">開啟</a></td>
                <td>
                  <Link to={`/label/${it.task_id}/${it.event_id}`}>重新標註</Link>
                  {' · '}
                  <button
                    className="linkish"
                    onClick={() => setHistoryFor({ eventPk: it.id, eventId: it.event_id })}
                  >
                    歷史
                  </button>
                  {it.label && (
                    <>
                      {' · '}
                      <button
                        className="linkish"
                        onClick={async () => {
                          if (!confirm('確定要重置此標註?該事件將重新進入待標註佇列。')) return;
                          await apiDeleteLabel(it.label!.id);
                          await load();
                        }}
                      >
                        重置
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="pager">
        <button onClick={() => setPage(1)} disabled={page === 1}>« 首頁</button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹ 上一頁</button>
        <span>第 {page} / {pages} 頁</span>
        <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>下一頁 ›</button>
        <button onClick={() => setPage(pages)} disabled={page >= pages}>末頁 »</button>
      </div>

      {historyFor && (
        <LabelHistoryModal
          eventPk={historyFor.eventPk}
          eventId={historyFor.eventId}
          onClose={() => setHistoryFor(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

function getField(it: LabelEvent, key: string): string | number {
  switch (key) {
    case 'time_seconds': return it.label?.data.time_seconds ?? -Infinity;
    case 'event_id': return it.event_id;
    case 'task_id': return it.task_id;
    case 'labeler': return it.label?.labeler_email ?? '';
    case 'labeled_at': return it.label?.labeled_at ?? '';
    default: return '';
  }
}
